import React, { useState } from "react";
import { Card, CardContent, Typography, Box, Divider, Grid, Collapse, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { MatxLayoutSettings } from "app/components/MatxLayout/settings";
import { formatCurrency } from "../utils/dashboardUtils";

const SalesSummary = ({ summaryData }) => {
  const [expandedStates, setExpandedStates] = useState({});

  const products = summaryData?.products || [];

  const toggleExpand = (productId) => {
    setExpandedStates((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const getProductDisplayName = (product) => {
    const isTickets =
      product.categories?.some(
        (cat) => cat.category_name?.toLowerCase() === "tickets"
      ) || product.product_name?.toLowerCase() === "tickets";

    if (isTickets) {
      return "Registrations";
    }
    return product.product_name;
  };

  const getSoldCount = (v) => {
    if (typeof v.sold_count === "object" && v.sold_count !== null) {
      return v.sold_count.total_stay || 0;
    }
    return v.sold_count || 0;
  };

  const renderItem = (label, value, idx) => (
    <Box
      key={idx}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{ gap: 1, flexWrap: "nowrap" }}
    >
      <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight="500"
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        <Typography variant="body1" fontWeight="700">
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Card
      sx={{
        borderRadius: "12px",
        border: `1px solid ${MatxLayoutSettings.palette.borderGrey}`,
        boxShadow: "none",
        mt: 4,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" fontWeight="700" gutterBottom>
          Sales Summary
        </Typography>
        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={{ xs: 2, md: 4 }}>
          {products.map((product) => {
            const productTotalSale =
              product.categories?.reduce((sum, cat) => {
                return (
                  sum +
                  (cat.variations?.reduce(
                    (vSum, v) => vSum + (v.sale_amount || 0),
                    0
                  ) || 0)
                );
              }, 0) || 0;

            const variations =
              product.categories?.flatMap((cat) => cat.variations || []) || [];

            return (
              <Grid item xs={12} md={6} key={product.product_id}>
                <Box
                  sx={{
                    borderLeft: `3px solid ${MatxLayoutSettings.palette.active}`,
                    pl: 2,
                    py: 0.5,
                  }}
                >
                  <Box
                    onClick={() => toggleExpand(product.product_id)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      cursor: "pointer",
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="700"
                        sx={{ color: MatxLayoutSettings.palette.black }}
                      >
                        {getProductDisplayName(product)} ({products.find(p => p.product_id === product.product_id)?.categories?.flatMap(cat => cat.variations || []).reduce((sum, v) => sum + getSoldCount(v), 0) || 0})
                      </Typography>
                      <Typography
                        variant="caption"
                        color={MatxLayoutSettings.palette.textBlack}
                        fontWeight="600"
                      >
                        Total Sales:{" "}
                        <Box
                          component="span"
                          sx={{
                            color: MatxLayoutSettings.palette.black,
                            fontWeight: "800",
                          }}
                        >
                          {formatCurrency(productTotalSale)}
                        </Box>
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      sx={{ mt: -0.5, pointerEvents: "none" }}
                    >
                      {expandedStates[product.product_id] ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                  <Collapse
                    in={expandedStates[product.product_id]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={1.5}
                      sx={{ mt: 1.5 }}
                    >
                      {variations.map((v, vIdx) => {
                        const qty = getSoldCount(v);
                        return renderItem(
                          `${v.variation_name} (${v.sold_count.total_stay ?? v.sold_count ?? 0})`,
                          formatCurrency(v.sale_amount || 0),
                          vIdx
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SalesSummary;
